import { TextLink } from './TextLink';

export interface CardFooterPromptProps {
  /** The question. Ends where the link begins: "New to Xonera?" */
  prompt: string;
  /** The link that answers it: "Create an account". */
  actionLabel: string;
  onAction: () => void;
}

/**
 * The ruled-off line at the foot of a card that sends you to a sibling screen.
 *
 * **The rule is the card's footer, not decoration.** It separates "here is
 * another way in" from "you are on the wrong screen" — the two things the
 * bottom of an auth card has to say, which read as one paragraph without it.
 *
 * Extracted from `AuthForm` and `SetNewPasswordCard`, where the markup was
 * byte-identical — same border, same padding, same `text-body`, same
 * `emphasis='strong'` link. Two copies of a thing whose entire job is to look
 * the same on every screen a user is bounced between.
 *
 * The prompt and the label are separate props rather than one `children`, so
 * the space between them cannot be lost: written as JSX children it is a
 * `{' '}` that a formatter is free to move onto its own line, and did.
 */
export function CardFooterPrompt({
  prompt,
  actionLabel,
  onAction,
}: CardFooterPromptProps) {
  return (
    <p className='border-border/60 text-body text-muted-foreground border-t pt-5 text-center'>
      {prompt}{' '}
      {/* `tap-target` (apps/web globals.css): a ~22px line, tapped as 44 on a
          phone. The prompt sits alone at the foot of a card, so the tap area
          overlaps nothing; plain text around it is not a target. */}
      <TextLink emphasis='strong' onClick={onAction} className='tap-target'>
        {actionLabel}
      </TextLink>
    </p>
  );
}
